import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { MapPin, ArrowUpRight, MessageSquareHeart } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchBranches } from "../../data/api";

const origin = typeof window !== "undefined" ? window.location.origin : "";

const BranchCard = ({ branch, i }) => {
  const feedbackUrl = `${origin}/feedback?branch=${branch.slug}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${branch.lat},${branch.lng}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
      data-testid={`branch-card-${branch.slug}`}
      className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#F59E0B]/40 transition-colors duration-500"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display uppercase text-2xl text-[#F8F4EC] tracking-tight leading-none">
            {branch.name}
          </h3>
          {branch.opened && (
            <span className="mt-2 inline-block text-[10px] tracking-[0.15em] uppercase text-[#F59E0B] border border-[#F59E0B]/30 rounded-full px-2 py-0.5">
              Since {branch.opened}
            </span>
          )}
        </div>
        {/* QR — scannable */}
        <div className="shrink-0 rounded-xl bg-white p-2 shadow-lg" title="Scan to give feedback">
          <QRCodeCanvas value={feedbackUrl} size={78} level="M" fgColor="#141110" bgColor="#ffffff" />
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[#BFAFA6] text-sm leading-relaxed">
        <MapPin size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />
        <span>{branch.address}</span>
      </div>

      <div className="mt-auto pt-5 flex flex-wrap items-center gap-4">
        <Link
          to={`/feedback?branch=${branch.slug}`}
          data-testid={`branch-feedback-${branch.slug}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F59E0B] text-[#141110] text-sm font-semibold hover:shadow-[0_0_22px_rgba(245,158,11,0.4)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
        >
          <MessageSquareHeart size={15} />
          Give Feedback
        </Link>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`branch-directions-${branch.slug}`}
          className="inline-flex items-center gap-1 text-sm text-[#F8F4EC]/80 hover:text-[#F59E0B] transition-colors"
        >
          Directions <ArrowUpRight size={14} />
        </a>
      </div>
    </motion.div>
  );
};

const MasterQR = () => {
  const url = `${origin}/feedback`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      data-testid="master-qr"
      className="w-full lg:w-auto shrink-0 rounded-2xl border border-[#F59E0B]/40 bg-[#1F1A18] p-6 flex flex-col items-center text-center"
    >
      <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#F59E0B]">
        Feedback QR
      </span>
      <div className="mt-4 rounded-xl bg-white p-3 shadow-lg">
        <QRCodeCanvas value={url} size={512} level="M" fgColor="#141110" bgColor="#ffffff" className="!h-[168px] !w-[168px]" />
      </div>
      <p className="mt-4 max-w-[200px] text-sm text-[#BFAFA6] leading-relaxed">
        Scan to share a compliment or complaint — just pick your branch.
      </p>
    </motion.div>
  );
};

export const Branches = () => {
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    fetchBranches().then(setBranches).catch(() => setBranches([]));
  }, []);
  return (
    <section
      id="branches"
      data-testid="branches-section"
      className="relative bg-[#141110] px-6 lg:px-10 py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14 mb-14">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#F59E0B]">
              Our Branches
            </span>
            <h2 className="mt-5 font-display uppercase text-[#F8F4EC] text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              {branches.length || 13} outlets, <span className="text-[#F59E0B]">one taste</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#BFAFA6]">
              Scan a branch’s QR code (or tap “Give Feedback”) to share a
              compliment or a complaint for that specific outlet — it helps us
              know exactly where to improve and who to thank.
            </p>
          </div>
          <MasterQR />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((b, i) => (
            <BranchCard key={b.slug} branch={b} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
